'use client';

import type { QuestionNote } from '@/_types/models/questionnaire';
import { Button, ButtonGroup, Flex, Notes, Input, Dialog } from '@/_components';
import { FC, useState, useContext } from 'react';
import { PreviewNotesDialogProps } from './PreviewNotesDialog.types';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { addNote, deleteNote } from '@/_api/client-questionnaires';

export const PreviewNotesDialog: FC<PreviewNotesDialogProps> = ({
  isOpen,
  companyId,
  questionId,
  notes,
  onClose,
  reloadQuestionnairePreview,
}) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [newNote, setNewNote] = useState<string>('');

  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  const onNotesSend = async (e: any) => {
    e.preventDefault();
    setLoading(true);

    try {
      await addNote({
        questionId,
        text: newNote,
        companyId,
        token,
      });

      await reloadQuestionnairePreview();
      addToast('success', 'Note added successfully');
    } catch (error) {
      console.error(error);
    } finally {
      setNewNote('');
      setLoading(false);
    }
  };

  const onDeleteNote = async (note: QuestionNote) => {
    if (!note?.id) return;

    setLoading(true);

    try {
      await deleteNote({
        noteId: note?.id,
        questionId,
        companyId,
        token,
      });
      await reloadQuestionnairePreview();

      addToast('success', 'Note deleted successfully');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
    }
  };

  const onCloseLocal = () => onClose();

  return (
    <Dialog isOpen={isOpen} onClose={onCloseLocal} title="Notes">
      <Dialog.Content>
        <Flex isColumn={true} gap={6}>
          <Notes notes={notes} onDeleteNote={onDeleteNote} />
          <form onSubmit={onNotesSend}>
            <Flex gap={4} isColumn={true}>
              <Input
                label="Message"
                type="text"
                placeholder="Your note"
                isLabelHidden={true}
                isLoading={isLoading}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <ButtonGroup align="right">
                <Button
                  onClick={onCloseLocal}
                  value="Cancel"
                  intent="secondary"
                />
                <Button
                  value="Add Message"
                  intent="primary"
                  type="submit"
                  isDisabled={isLoading}
                  isLoading={isLoading}
                />
              </ButtonGroup>
            </Flex>
          </form>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
